/**
 * Date-only helpers.
 *
 * Dates in this app ("purchased on 3 Sep") are calendar dates, not instants.
 * They are stored and passed around as ISO 'YYYY-MM-DD' strings and converted
 * to integer day numbers for arithmetic. The arithmetic goes through UTC so a
 * server in Karachi and a phone on a plane agree on what day it is; only
 * `todayIso` needs a timezone, because "today" is a question about a place.
 */

export type IsoDate = string

const MS_PER_DAY = 86_400_000

/** The trader's day. Every tenant is in Pakistan; this is the only default. */
export const DEFAULT_TIME_ZONE = 'Asia/Karachi'

/** Days since the Unix epoch. The unit all interval maths uses. */
export function toDayNumber(date: IsoDate): number {
  const [y, m, d] = date.split('-').map(Number)
  return Math.floor(Date.UTC(y, m - 1, d) / MS_PER_DAY)
}

export function fromDayNumber(day: number): IsoDate {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10)
}

// `en-CA` renders a numeric date as YYYY-MM-DD, which is already the storage
// format, so no reassembly is needed. Built lazily and cached because an
// Intl.DateTimeFormat costs more than the formatting call it performs.
const ISO_FORMATTERS = new Map<string, Intl.DateTimeFormat>()

function isoFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = ISO_FORMATTERS.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    ISO_FORMATTERS.set(timeZone, formatter)
  }
  return formatter
}

/**
 * Today's calendar date where the trader is standing.
 *
 * NOT `new Date().toISOString().slice(0,10)`. Between 00:00 and 05:00 PKT the
 * UTC date is still yesterday, so that version silently backdates every
 * purchase and shortens every days-held by one for anyone entering a deal
 * after midnight — which, at a mandi, is a normal hour to be working.
 *
 * Resolved by ICU rather than a hardcoded +05:00. Pakistan has no DST today,
 * but an offset written into the source is a fact that can quietly stop being
 * true; a timezone name cannot.
 */
export function todayIso(timeZone: string = DEFAULT_TIME_ZONE, now: Date = new Date()): IsoDate {
  return isoFormatter(timeZone).format(now)
}

/**
 * The current instant, as a UTC ISO-8601 timestamp.
 *
 * The counterpart to `todayIso`, and the reason `new Date().toISOString()` can
 * stay banned everywhere else: a timestamp and a calendar date are written
 * almost identically and mean entirely different things. Confusing the two is
 * exactly how a purchase gets backdated. Ask for `nowIso` when you want the
 * moment something happened, and `todayIso` when you want the trader's day.
 */
export function nowIso(now: Date = new Date()): string {
  return now.toISOString()
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return fromDayNumber(toDayNumber(date) + days)
}

/** Whole days from `from` to `to`. Negative if `to` precedes `from`. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return toDayNumber(to) - toDayNumber(from)
}

export function isValidIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const d = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value
}

export function minDate(a: IsoDate, b: IsoDate): IsoDate {
  return a <= b ? a : b
}

export function maxDate(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b
}

// ---------------------------------------------------------------------------
// Half-open day intervals: [start, end) in day numbers.
//
// Half-open is the convention throughout because it makes the day-boundary
// rule fall out for free: an animal bought and sold on the same day spans
// zero days and correctly carries no feed cost.
// ---------------------------------------------------------------------------

export interface DayInterval {
  /** Inclusive. */
  start: number
  /** Exclusive. */
  end: number
}

export function interval(startIso: IsoDate, endExclusiveIso: IsoDate): DayInterval {
  return { start: toDayNumber(startIso), end: toDayNumber(endExclusiveIso) }
}

/** Build from an inclusive end date, e.g. a bill covering 1–31 January. */
export function inclusiveInterval(startIso: IsoDate, endInclusiveIso: IsoDate): DayInterval {
  return { start: toDayNumber(startIso), end: toDayNumber(endInclusiveIso) + 1 }
}

export function intervalLength(i: DayInterval): number {
  return Math.max(0, i.end - i.start)
}

/** Number of days two intervals share. Zero when they do not touch. */
export function overlapDays(a: DayInterval, b: DayInterval): number {
  return Math.max(0, Math.min(a.end, b.end) - Math.max(a.start, b.start))
}

export function intersect(a: DayInterval, b: DayInterval): DayInterval | null {
  const start = Math.max(a.start, b.start)
  const end = Math.min(a.end, b.end)
  return end > start ? { start, end } : null
}
